import { DefaultSettings } from "../devexpress/settings/defaultsettings";
import { PanelVm, IPanelInitializer as PanelInitializer } from "./panel-vm";
import * as Dbtypes from "../types/shared-dbtypes";
import { ModuleHelper } from "../core/module/module-helper";
import { Logger } from "../logger/logger";
import { KoEvents } from "../models/ko-events";
import { dx } from "../devexpress/ui-options-types-wrapper";
import { ControlType } from "../devexpress/controltype";
import { KoExtend } from "../extend/knockout-extend";
import { DxOptionFactory } from "../devexpress/factory/dx-option-factory";
import { DateHelper } from "../helper/date-helper";
import { ObjectLinkCategory } from "../types/shared-enums";
import { Bit } from "../helper/bit";
import { StateStoring } from "../core/settings/statestoring";
import { UrlHelper } from "../core/html/url-helper";
import { DataGridOptions } from "../devexpress/ui-option-types";
import { ExportToExcel } from "../devexpress/export/export-data-grid";

export class ActionMenuItem {
	public id: number;
	public resolved: boolean;
	public options: KnockoutObservable<any>;
}

interface IGridMenuOptions<T> {
	showGoto: boolean;
	filterCategory?: number;
	getClassId: (menuData: any, itemData: T) => number;
	getObjectId: (menuData: any, itemData: T) => number;
}

export enum SelectionMode {
	None = "none",
	Single = "single",
	Multiple = "multiple"
}

export abstract class GridPanelVm<T> extends PanelVm {
	private previousActionMenuItemId: number = null; // Bug 27746

	constructor(init?: PanelInitializer) {
		super(init);
		init = init || {};
		this.useCustomAddLogic = init.useCustomAddLogic || false;
		this.gridFilterValue = ko.observable("");
		this.gridCurrentItem = ko.observable(null);
		this.gridCurrentSelection = ko.observableArray([]);
		this.gridCurrentSelection.subscribe((value) => {
			if (!value || value.length <= 0) {
				this.gridCurrentItem(null);
			}
		});

		this.gridItemSelected = ko.computed(() => {
			let curItem = this.gridCurrentItem();
			return curItem != null;
		});

		this.noGridItemSelected = ko.computed(() => {
			return !this.gridItemSelected();
		});

		this.gridFilter = ko.pureComputed({
			read: () => {
				return this.gridFilterValue();
			},
			write: (value: string) => {
				this.gridFilterValue(value);
				this.gridCurrentSelection([]);
			}
		});

		this.applyGridOptions = (options) => {
			if (!options) {
				options = {};
			}
			if (options.height == null) {
				options.height = this.contentHeightAsPixel;
			} else if (options.height > 0 && options.height < 210) {
				options.height = 210;
			}

			if (options.onRowClick == null) {
				options.onRowClick = this.handleDoubleClick;
			}

			let editorPreparingSearchTrimAndCustomAdd: DevExpress.ui.dxDataGridOptions["onEditorPreparing"] = (e: any) => {
				if (e.parentType === "searchPanel") {
					e.setValue = (value: string) => {
						e.component.searchByText(value ? value.trim() : value);
					};
				}
				if (this.useCustomAddLogic) {
					if (e && e.parentType === "dataRow") {
						e.editorOptions.disabled = true;
						if (e.editorName !== "dxCheckBox") {
							e.cancel = true;
						}
					}
				}
			};

			if (options.onEditorPreparing == null) {
				options.onEditorPreparing = editorPreparingSearchTrimAndCustomAdd;
			} else {
				let oldOnEditorPreparing = options.onEditorPreparing;
				options.onEditorPreparing = (e: any) => {
					oldOnEditorPreparing(e);
					editorPreparingSearchTrimAndCustomAdd(e);
				};
			}

			if (options.onEditingStart !== null) {
				let onEditingStart: DevExpress.ui.dxDataGridOptions["onEditingStart"] = (e) => {
					if (e?.column?.dataType === "boolean") {
						return;
					}
					setTimeout(() => this.dataGridInstance.selectRows([e.key], false));
				};

				if (options.onEditingStart == null) {
					options.onEditingStart = onEditingStart;
				} else {
					let oldOnEditingStart = options.onEditingStart;
					options.onEditingStart = (e: any) => {
						onEditingStart(e);
						oldOnEditingStart(e);
					};
				}
			}

			let newOnInitialized: DevExpress.ui.dxDataGridOptions["onInitialized"] = (e) => {
				this.dataGridInstance = e.component;
			};

			if (options.onInitialized == null) {
				options.onInitialized = newOnInitialized;
			} else {
				let implInitialize = options.onInitialized;
				options.onInitialized = (e: any) => {
					newOnInitialized(e);
					implInitialize(e);
				};
			}

			let newOnCellPrepared: DevExpress.ui.dxDataGridOptions["onCellPrepared"] = (e) => {
				if (e.rowType === "header") {
					let headerTitle = e.cellElement.children();
					headerTitle.attr('title', e.column.caption);
				}
			};
			if (options.onCellPrepared == null) {
				options.onCellPrepared = newOnCellPrepared;
			} else {
				let implOnCellPrepared = options.onCellPrepared;
				options.onCellPrepared = (e: any) => {
					newOnCellPrepared(e);
					implOnCellPrepared(e);
				};
			}

			let newOnContentReady: DevExpress.ui.dxDataGridOptions["onContentReady"] = (e) => {
				if (!this.firstContentReadyDone) {
					this.firstContentReadyDone = true;
					let columnAutoWidth = e.component.state()?.columnAutoWidth || false;
					this.columnAutoWidthActivated(columnAutoWidth);
					e.component.option("columnAutoWidth", columnAutoWidth);

					let columnResizeMode = e.component.state()?.columnResizingMode !== "nextColumn";
					this.columnResizeModeActivated(columnResizeMode);
					e.component.option("columnResizingMode", columnResizeMode ? "widget" : "nextColumn");

					e.element.css("max-width", "100%");
				}
			}

			if (options.onContentReady == null) {
				options.onContentReady = (e: any) => {
					newOnContentReady(e);
					if (this.useCustomAddLogic) {
						return;
					}

					let totalCount = e.component.totalCount();
					if (totalCount > 0) {
						e.component.selectRowsByIndexes([0]);
					} else {
						e.component.selectRowsByIndexes([]);
					}
				};
			} else {
				let implContentReady = options.onContentReady;
				options.onContentReady = (e: any) => {
					newOnContentReady(e);
					implContentReady(e);
				}
			}

			if (options.onSelectionChanged == null) {
				options.onSelectionChanged = (data: { selectedRowsData: T[] }) => {
					if (data && data.selectedRowsData && data.selectedRowsData.length > 0) {
						let rowData = data.selectedRowsData[0];
						this.gridCurrentItem(rowData);
					} else {
						this.gridCurrentItem(null);
					}
				};
			}

			if (options.selectedRowKeys == null) {
				options.selectedRowKeys = this.gridCurrentSelection;
			}

			if (options.searchPanel == null) {
				options.searchPanel = {
					text: this.gridFilter as any,
				};
			}

			if (options.stateStoring == null) {
				let className = this.constructor.name;
				Logger.warn("No state storing set for grid in class " + className, options);
			} else if (UrlHelper.isDebuggingEnabled()) {
				StateStoring.registeredStateStoringChanges().find((item) => item.stateStoring === options.stateStoring)?.hasSavedData?.subscribe((newValue) => {
					if (!this.dataGridInstance) {
						return;
					}

					if (newValue) {
						$(this.dataGridInstance.element()).addClass("nautilus-state-storing-saved");
					} else {
						$(this.dataGridInstance.element()).removeClass("nautilus-state-storing-saved");
					}
				});
			}

			if (options.summary != null) {
				let summary: any = options.summary;
				if (summary.texts == null) {
					summary.texts = {
						sum: "{0}"
					}
				}
			}

			if (Array.isArray(options.columns)) {
				for (let column of options.columns as dx.DataGridColumn[]) {
					if (column.dataType === "date" || column.dataType === "datetime") {
						if (column.editorOptions == null) {
							column.editorOptions = {};
						}

						if (column.editorOptions.useMaskBehavior == null) {
							column.editorOptions.useMaskBehavior = true;
						}
					}
					if (column.dataType === "date") {
						if (column.width === undefined) {
							column.width = DefaultSettings.widthDate;
						}
					}
					if (column.dataType === "datetime") {
						if (column.width == null) {
							column.width = DefaultSettings.widthDateTime;
						}
					}
					if (column.dataType === "boolean") {
						if (column.alignment == null) {
							column.alignment = DefaultSettings.booleanAlignment;
						}
						if (column.width == null) {
							column.width = DefaultSettings.widthBoolean;
						}
					}
					if (column.dataType === "number") {
						// if width is not defined, format is defined (e.g. currency) and lookup is not defined (Lookup leads to text, so we don't want width automatically)
						if (column.width === undefined && column.format != null && column.lookup === undefined) {
							column.width = DefaultSettings.widthNumber;
						}
					}
				}
			}

			// breaks for "truly" dynamic grids
			if (Array.isArray(KoExtend.resolve(options.columns))) {
				for (let column of KoExtend.resolve(options.columns) as dx.DataGridColumn[]) {
					if (column.allowEditing || (KoExtend.resolve(options.editing)?.allowUpdating && column.allowEditing !== false)) {
						let existingCss = column.cssClass;
						if (existingCss == null) {
							column.cssClass = "nautilus-column-editable";
						} else if (!(existingCss.indexOf("nautilus-column-editable") >= 0)) {
							column.cssClass = (column.cssClass ?? "") + " nautilus-column-editable";
						}
					}
				}
			}

			if (options.columns != null) {
				let cols = options.columns;
				let checkCols = (columns: Array<dx.DataGridColumn | string>) => {
					if (!Array.isArray(columns)) {
						return;
					}

					let beganGroup = false;
					let beginGroup = () => {
						if (!beganGroup) {
							let className = this.constructor.name;
							Logger.beginGroupCollapsed("Column validation for class " + className);
							beganGroup = true;
						}
					};

					for (let col of columns) {
						if (typeof col !== "object") {
							continue;
						}

						let name = col.dataField || col.caption || "N/A";

						if (col.dataType == null && name !== "Aktion") {
							beginGroup();
							Logger.warn(`Missing dataType option for column '${name}'.`, col);
						}

						if (col.dataType === "date" && col.format == null) {
							beginGroup();
							Logger.warn(`Missing format options for date column '${name}'.`, col);
						}

						if (col.caption === "Aktion" && col.allowHiding == null) {
							col.allowHiding = false;
							beginGroup();
							Logger.log(`Hiding action disabled for column '${name}'.`, col);
						}
					}

					if (beganGroup) {
						Logger.endGroup();
					}
				};

				if (ko.isObservable(cols)) {
					let subs = cols.subscribe((newValue) => {
						if (!newValue) {
							return;
						}
						if (subs != null) {
							subs.dispose();
							return;
						}
						checkCols(newValue);
					});
				} else {
					checkCols(cols);
					cols.push(DefaultSettings.getLastColumnForComputed());
				}
			}

			options.onExporting = (e) => {
				ExportToExcel.exportDataGrid(e);
			}

			this.gridOptions = DefaultSettings.mergeWithDefault(ControlType.DataGrid, options);
		};

		this.applyGridItemMenuOptions = (items, dispatchMenuItemClick, options, menuOptions) => {
			let showGoto = options && options.showGoto;
			let filterCategory = (options?.filterCategory ?? ObjectLinkCategory.All);
			let curItems: any = items;

			if (showGoto) {
				curItems = [
					{
						text: "Bitte warten",
						icon: "fa fa-spinner fa-pulse fa-3x fa-fw"
					}
				];
			}
			this.gridItemMenuOptions = {
				cssClass: "nautilus-grid-item-buttons",
				showFirstSubmenuMode: {
					name: "onClick",
					delay: { show: 0, hide: 300 }
				},
				dataSource: ko.isComputed(curItems) ? ko.computed(() => {
					return [{
						id: 0,
						text: "",
						icon: "fa fa-bars",
						items: curItems(),
					}];
				}) : ko.observableArray([{
					id: 0,
					text: "",
					icon: "fa fa-bars",
					items: curItems,
				}]),
				onItemClick: async (data: any) => {
					if (data && data.itemData) {
						let curId: number = data.itemData.id;
						let closeMenuOnClick = data.itemData.closeMenuOnClick;
						if (curId !== 0 && closeMenuOnClick !== false) {
							this.closeItemMenu(data);
						} else if (curId === 0 && showGoto) {
							await this.gotoAsync(data, options, filterCategory, items);
						}

						if (dispatchMenuItemClick) {
							dispatchMenuItemClick(curId, data);
						}
					}
				},
			};

			if (menuOptions) {
				$.extend(true, this.gridItemMenuOptions, menuOptions);
			}
		};

		this.deselectItems = () => {
			this.gridCurrentSelection([]);
			this.gridCurrentItem(null);
		};

		if (this.useCustomAddLogic) {
			KoEvents.addSaveCompleteHandler(this.saveComplete, this);
			KoEvents.addDeleteCompleteHandler(this.deleteComplete, this);
			KoEvents.addCopyCompleteHandler(this.copyComplete, this);
			KoEvents.addNewCompleteHandler(this.newComplete, this);
		}

		if (Logger.isEnabled()) {
			Promise.resolve().then(() => {
				if (this.gridOptions == null) {
					Logger.warn(`Did you forget to call applyGridOptions() in class ${this.constructor.name}?`);
				}
			});
		}
	}

	/** List of ids filled by selection column if exists */
	public chooseColumnIds: KnockoutObservableArray<number> = ko.observableArray([]);
	/** Filled by DevExpress Grid */
	public gridCurrentSelection: KnockoutObservableArray<any>;
	/** Filled by DevExpress Grid */
	public gridCurrentItem: KnockoutObservable<T>;
	public gridItemSelected: KnockoutComputed<boolean>;
	public noGridItemSelected: KnockoutComputed<boolean>;
	public deselectItems: () => void;

	public addRowTemplateOptions: dx.ButtonOptions = {
		icon: "add",
		hint: "Hinzufügen",
		onClick: () => this.addRowTemplateClickAsync(),
	};

	protected gridFilterValue: KnockoutObservable<string>;
	protected gridFilter: KnockoutComputed<string>;
	protected gridOptions: any | DataGridOptions;
	// to save filters before reloading and reapply them afterwards
	protected oldCoulumnHeaderFilters: any[] = [];
	protected applyGridOptions: (options: dx.DataGridOptions) => void;
	protected gridItemMenuOptions: any;
	protected actionMenuItems: ActionMenuItem[] = [];
	protected dataGridInstance: DevExpress.ui.dxDataGrid;
	protected useCustomAddLogic: boolean = false;
	protected columnAutoWidthActivated: KnockoutObservable<boolean> = ko.observable(false);
	protected columnResizeModeActivated: KnockoutObservable<boolean> = ko.observable(false);
	protected firstContentReadyDone: boolean = false

	// double click infos
	protected clickKey: any = null;
	protected clickDate: number = null;
	protected clickCount: number = 0;

	protected applyGridItemMenuOptions: (
		items: any[] | KnockoutComputed<any[]>,
		dispatchMenuItemClick?: (menutItemId: number, menuData?: any) => void,
		options?: IGridMenuOptions<T>,
		menuOptions?: dx.MenuOptions) => void;

	public async reload(deselectAll?: boolean) {
		let dataSource = KoExtend.resolve(this.gridOptions?.dataSource);
		if (dataSource?.reload) {
			await dataSource.reload();
			let ds = this.gridOptions.dataSource;
			if (ko.isObservable(ds)) {
				ds.notifySubscribers();
			}
		} else if (this.dataGridInstance) {
			await Promise.resolve(this.dataGridInstance.refresh());
		} else {
			Logger.warn("Could not reload datasource");
		}

		if (deselectAll) {
			this.deselectItems();
		}
	}

	public hideColumnChooser() {
		this.dataGridInstance?.hideColumnChooser();
	}

	public getActionMenuItemOption = (id: number): KnockoutObservable<any> => {
		let actionMenuItem = this.getActionMenuItem(id);
		return actionMenuItem.options;
	}

	public closeItemMenu: (dxMenu: any) => void = (dxMenu) => {
		// close menu
		try {
			if (dxMenu && dxMenu.component && dxMenu.component.hide) {
				dxMenu.component.hide();
			}
		} catch (err) {
			Logger.error("Error while closing menu: ", err);
		}
	}

	public beginCustomLoad(msg: string = "Daten werden geladen...") {
		this.dataGridInstance?.beginCustomLoading(msg);
	}

	public endCustomLoad() {
		this.dataGridInstance?.endCustomLoading();
	}

	protected deleteComplete() {
		this.deselectItems();
		this.changeComplete();
		this.reload(true);
	}

	protected newComplete() {
		this.deselectItems();
		if (this.dataGridInstance) {
			this.dataGridInstance.addRow();
		}
	}

	protected copyComplete(newSelection: any[]) {
		this.saveComplete(newSelection);
	}

	protected async refreshComplete() {
		return this.reload(true);
	}

	protected currentOrder(): DevExpress.ui.dxDataGridColumn[] {
		if (!this.dataGridInstance) {
			Logger.error("No gridinstance set to order");
			return [];
		}

		let sortedColumns = this.dataGridInstance.getVisibleColumns().filter((col) => col.sortOrder);
		return sortedColumns || [];
	}

	protected currentKeysInOrder(): any[] {
		if (!this.dataGridInstance) {
			Logger.error("No gridinstance set to order");
			return [];
		}
		let index = 0;
		let result: any[] = [];
		let curKey = this.dataGridInstance.getKeyByRowIndex(index);
		while (curKey != null) {
			result.push(curKey);
			curKey = this.dataGridInstance.getKeyByRowIndex(++index);
		}
		return result;
	}

	protected changeComplete() {
		if (this.dataGridInstance && this.useCustomAddLogic) {
			this.dataGridInstance.cancelEditData();
		}
	}

	protected saveComplete(newSelection: any[]) {
		if (this.dataGridInstance) {
			this.changeComplete();
			this.deselectItems();

			this.dataGridInstance.refresh().always(() => {
				if (newSelection && newSelection.length > 0) {
					// TODO (pr) workaround because stale data
					this.gridCurrentSelection(newSelection);
				}
			});
		}
	}

	protected getActionMenuItem = (id: number): ActionMenuItem => {
		let actionMenuItem = this.actionMenuItems.find((value) => value.id === id);
		if (!actionMenuItem) {
			let currentMenuItems: any[] = [];
			$.extend(true, currentMenuItems, this.gridItemMenuOptions);
			actionMenuItem = {
				id,
				resolved: false,
				options: ko.observable(currentMenuItems)
			};

			this.actionMenuItems.push(actionMenuItem);
		}

		return actionMenuItem;
	}

	protected getSelectionMode() {
		let mode = SelectionMode.None;
		if (this.gridOptions && this.gridOptions.selection && this.gridOptions.selection.mode) {
			mode = this.gridOptions.selection.mode;
		}

		return mode;
	}

	protected reloadStateStoringStateAsync = async () => {
		let options: DevExpress.ui.dxDataGridOptions = this.gridOptions;
		if (this.dataGridInstance && options && options.stateStoring && options.stateStoring.customLoad) {
			let state = await Promise.resolve(options.stateStoring.customLoad());
			this.dataGridInstance.state(state || {});
		} else {
			if (!this.dataGridInstance) {
				Logger.error("StateStoring: Grid not initialized", options);
			} else if (!options) {
				Logger.error("StateStoring: Options not initialized", this.dataGridInstance);
			} else if (!options.stateStoring) {
				Logger.error("StateStoring: Options stateStoring not initialized", options);
			} else if (!options.stateStoring.customLoad) {
				Logger.error("StateStoring: No custom load options set to reload state storing state async", options);
			} else {
				Logger.error("Cannot reload state storing state.");
			}
		}
	}

	/**
	 * Call this in onContentReady or later
	 */
	protected fillWindowHeight = (updateOnResize: boolean = true, padding?: number) => {
		let element = this.dataGridInstance.element().find("table.dx-datagrid-table");
		let height = window.innerHeight - element.offset().top;
		if (padding) {
			height -= padding;
		}
		this.dataGridInstance.option("height", Math.max(height, 300));

		if (updateOnResize) {
			let resizeTimerHandle: number;
			$(window).resize(() => {
				clearTimeout(resizeTimerHandle);
				resizeTimerHandle = window.setTimeout(() => this.fillWindowHeight(false, padding), 100);
			});
		}
	}

	protected selectOnCellClick(e: any) {
		// select cell if clicked on and not selected
		if (this.gridCurrentItem() !== e.data) {
			this.dataGridInstance.closeEditCell();
			this.gridCurrentSelection([e.data]);
		}
	}

	protected doubleClickAction(data: T) {
		Logger.error("Double click not handled", data);
	}

	protected handleDoubleClick: DevExpress.ui.dxDataGridOptions["onRowClick"] = (e: any) => {
		let initialClick = () => {
			this.clickCount = 1;
			this.clickKey = e.key;
			this.clickDate = (DateHelper.nowAsDateWithTime()).getTime();
		};

		let doubleClick = () => {
			this.clickCount = 0;
			this.clickKey = 0;
			this.clickDate = null;
		};

		if ((!this.clickCount) || (this.clickCount !== 1) || (this.clickKey !== e.key)) {
			initialClick();
		} else if (this.clickKey === e.key) {
			let date: number = (DateHelper.nowAsDateWithTime()).getTime();
			if ((date - this.clickDate) <= 300) {
				doubleClick();
				this.doubleClickAction(e.data);
			} else {
				initialClick();
			}
		}
	}

	/**
	 * Overwrite to set function for add row button
	 */
	protected async addRowTemplateClickAsync(): Promise<void> {
		throw new Error("Forgot to overwrite addRowTemplateClick");
	}

	/**
	 * Overwrite to set function for chooseOptionsDisabled
	 */
	protected chooseOptionDisabledFilter(rowData: any): boolean | KnockoutObservable<boolean> {
		return false;
	}

	protected getChooseOptions(rowData: any, key = "id") {
		return DxOptionFactory.getInstance().createOptions({
			type: ControlType.CheckBox,
			options: {
				disabled: this.chooseOptionDisabledFilter(rowData),
				value: ko.pureComputed({
					read: () => {
						return this.chooseColumnIds.indexOf(KoExtend.resolve(rowData[key])) >= 0;
					},
					write: (newValue) => {
						if (newValue) {
							this.chooseColumnIds.push(KoExtend.resolve(rowData[key]));
						} else {
							this.chooseColumnIds.remove(KoExtend.resolve(rowData[key]));
						}
					}
				})
			}
		});
	}

	protected getChooseControlMenu(extraItems?: any[], dataSource?: KnockoutObservableArray<T> | T[] | KnockoutComputed<T[]>, key = "id") {
		let items = this.getSelectionControlOptions(dataSource, key);

		if (extraItems && extraItems.length > 0) {
			items = items.concat(extraItems);
		}

		return {
			widget: "dxMenu",
			options: {
				items: [
					{
						text: "",
						icon: "fa fa-bars",
						items
					}
				]
			},
			location: "after"
		};
	}

	protected getSelectionControlOptions(dataSource?: KnockoutObservableArray<T> | T[] | KnockoutComputed<T[]>, key = "id"): any[] {
		return [{
			text: "Keinen auswählen",
			icon: "fa fa-square-o",
			disabled: ko.computed(() => this.chooseColumnIds().length === 0),
			onClick: (data: any) => {
				try {
					this.closeItemMenu(data);
					this.chooseColumnIds([]);
				} catch (err) {
					Logger.error("Error while removing selection ", err);
				}
			}
		},
		{
			text: "Alle auswählen",
			icon: "fa fa-th-list",
			onClick: (data: any) => {
				try {
					let grid = this.dataGridInstance;
					if (!grid) {
						return;
					}
					this.closeItemMenu(data);

					let items: any[] = dataSource != null ? KoExtend.resolve(dataSource) : grid.getDataSource()?.items();
					let itemsFiltered = items.filter((item) => !this.chooseOptionDisabledFilter(item));
					if (itemsFiltered && itemsFiltered.length > 0) {
						let allIds = itemsFiltered.map((item) => KoExtend.resolve(item[key]));
						this.chooseColumnIds(allIds);
					}
				} catch (err) {
					Logger.error("Error while selecting all elements ", err);
				}
			}
		},
		{
			text: "Auswahl invertieren",
			icon: "fa fa-check-square",
			onClick: async (data: any) => {
				try {
					let grid = this.dataGridInstance;
					if (!grid) {
						return;
					}
					grid.beginUpdate();
					this.closeItemMenu(data);

					let items: any[] = dataSource != null ? KoExtend.resolve(dataSource) : grid.getDataSource()?.items();
					let itemsFiltered = items.filter((item) => !this.chooseOptionDisabledFilter(item));
					if (itemsFiltered && itemsFiltered.length > 0) {
						let oldSelection = this.chooseColumnIds();
						this.chooseColumnIds([]);
						let allIds = itemsFiltered.map((item) => KoExtend.resolve(item[key]));
						window.setTimeout(() => {
							for (let id of allIds) {
								if (oldSelection.indexOf(id) === -1) {
									this.chooseColumnIds.push(id);
								}
							}
						}, 0);
					}
					grid.endUpdate();
				} catch (err) {
					Logger.error("Error while inverting selection ", err);
				}
			}
		}];
	}

	/**
	 * Saves the current column HeaderFilter Values
	 * Can be reapplied with reapplyOldColumnFilters()
	 */
	protected getOldColumnFilters() {
		this.oldCoulumnHeaderFilters = [];
		let colCount = this.dataGridInstance.columnCount();
		for (let i = 0; i < colCount; i++) {
			let filter = this.dataGridInstance.columnOption(i, "filterValues");
			this.oldCoulumnHeaderFilters[i] = filter;
		}

		return this.oldCoulumnHeaderFilters;
	}

	/**
	 * Reapplies column HeaderFilter Values saved by calling getOldColumnFilters()
	 */
	protected reapplyOldColumnFilters() {
		let colCount = this.dataGridInstance.columnCount();
		for (let i = 0; i < colCount; i++) {
			if (this.oldCoulumnHeaderFilters[i]) {
				this.dataGridInstance.columnOption(i, "filterValues", this.oldCoulumnHeaderFilters[i]);
			}
		}
		if (this.oldCoulumnHeaderFilters.some((val) => val != null)) {
			this.dataGridInstance.refresh();
		}
	}

	private async gotoAsync(e: any, options: IGridMenuOptions<T>, filterCategory: number, items: any[] | KnockoutComputed<any[]>) {
		let itemData: T = e.model?.data;
		let classId = options.getClassId(e, itemData);
		let objectId = options.getObjectId(e, itemData);

		if (!classId && !objectId) {
			Logger.log("Not enough data to create goto menu", { classId, objectId });
			return;
		}

		let actionMenuItem = this.getActionMenuItem(objectId);
		if (actionMenuItem.resolved && this.previousActionMenuItemId === objectId) {
			return;
		}

		let resultData: Dbtypes.IObjectLinkResult[] = await ModuleHelper.getObjectLinkResultsByClassIdAsync(classId, objectId);
		resultData = resultData.filter((item) => item.category === 0 || Bit.and(filterCategory, item.category));
		actionMenuItem.resolved = true;
		this.previousActionMenuItemId = objectId;

		let currentOptions = actionMenuItem.options();
		if (!currentOptions.dataSource && !currentOptions.dataSource().length) {
			return;
		}

		let dataSource = currentOptions.dataSource();
		let newItems: any[] = [];
		$.extend(true, newItems, items);
		dataSource[0].items = newItems;
		dataSource[0].selected = "selected";
		dataSource[0].items.push({
			text: "Gehe zu",
			icon: "fa fa-external-link",
			closeMenuOnClick: false,
			items: [],
			disabled: !resultData?.length,
		});

		if (resultData?.length > 0) {
			let lastItem = dataSource[0].items.length - 1;
			for (let value of resultData) {
				let newItem = {
					text: value.title,
					onClick: () => {
						ModuleHelper.openWindowInNewTab(value.path, !value.isRelative);
					}
				};

				dataSource[0].items[lastItem].items.push(newItem);
			}
		}

		currentOptions.dataSource(dataSource);
		actionMenuItem.options(currentOptions);

		if (e && e.element) {
			setTimeout(() => $(e.element).find(".dx-menu-item").eq(0).trigger("dxclick"), 0);
		}
	}
}